import { Api, ApiError } from "../api.js";

export interface Range {
  agentId?: string;
  /** Whole days, `2026-10-01`, both included. */
  from?: string;
  to?: string;
}
export interface ConvFilters extends Range {
  q?: string;
  test?: boolean;
}
export interface Summary {
  conversations: number;
  messages: number;
  avgMessagesPerConversation: number;
  avgMinutesPerConversation: number;
  totalMinutes: number;
  answered: number;
  grounded: number;
  unanswered: number;
  answeredFromKnowledgeRate: number | null;
  couldNotAnswerRate: number | null;
  thumbsUp: number;
  thumbsDown: number;
  leads: number;
  leadRate: number | null;
}
export interface Day {
  day: string;
  conversations: number;
  messages: number;
  activeSeconds: number;
  leads: number;
  thumbsUp: number;
  thumbsDown: number;
}
export interface Question {
  question: string;
  count: number;
}
export interface ConvRow {
  id: string;
  agentId: string;
  agentName: string;
  channel: string;
  isTest: boolean;
  startedAt: string;
  lastActivityAt: string;
  messages: number;
  activeSeconds: number;
  hasLead: boolean;
  firstQuestion: string;
  /** How the visitor arrived. Null for conversations from before tracking. */
  sourceLabel: string | null;
  sourceChannel: string | null;
  campaign: string | null;
}
export interface Attribution {
  landingUrl?: string;
  referrer?: string;
  params: Record<string, string>;
  channel: string;
  source: string;
  medium: string;
  campaign: string;
  label: string;
  confidence: "high" | "medium" | "low";
  firstSeenBy: "widget" | "derived";
  capturedAt?: string;
}
interface Counted {
  conversations: number;
  leads: number;
  leadRate: number | null;
}
export interface SourcesReport {
  channels: ({ channel: string } & Counted)[];
  sources: ({ channel: string; source: string; medium: string; label: string | null } & Counted)[];
  campaigns: ({ source: string; medium: string; campaign: string } & Counted)[];
  landingPages: ({ page: string } & Counted)[];
  submissionPages: ({ page: string } & Counted)[];
}
export interface ConvMessage {
  role: "user" | "assistant";
  content: string;
  status: string;
  mode: string | null;
  at: string;
  answerNo: number | null;
  vote: "up" | "down" | null;
}
export interface ConvDetail extends Omit<ConvRow, "firstQuestion"> {
  messageList: ConvMessage[];
  attribution: Attribution | null;
  submissionUrl: string | null;
}
export type Format = "csv" | "pdf" | "md";

const query = (o: Record<string, string | number | boolean | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== "" && v !== false) q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
};

/**
 * Calls for the Conversations and Statistics screens (docs/ANALYTICS.md). It reuses the app's address and sign-in headers
 * (`Api.url`, `authHeaders`, `fetchCredentials`), so it works with a person's session and with an API key.
 */
export class AnalyticsClient {
  constructor(
    private readonly api: Api,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async send(method: string, path: string, body?: unknown): Promise<Response> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.api.url(path), {
        method,
        headers: { ...this.api.authHeaders(), ...(body !== undefined ? { "content-type": "application/json" } : {}) },
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: this.api.fetchCredentials(),
      });
    } catch {
      throw new ApiError(0, "network", "network");
    }
    if (!res.ok) {
      let json: Record<string, unknown> = {};
      try {
        json = (await res.json()) as Record<string, unknown>;
      } catch {
        /* not JSON */
      }
      throw new ApiError(res.status, typeof json.error === "string" ? json.error : "server", typeof json.message === "string" ? json.message : "", [], json);
    }
    return res;
  }
  private async json<T>(method: string, path: string, body?: unknown): Promise<T> {
    return (await (await this.send(method, path, body)).json()) as T;
  }

  summary = (r: Range) => this.json<{ summary: Summary; canReadConversations: boolean; from: string; to: string }>("GET", `/v1/analytics/summary${query({ ...r })}`);
  daily = (r: Range) => this.json<{ days: Day[] }>("GET", `/v1/analytics/daily${query({ ...r })}`).then((x) => x.days);
  sources = (r: Range) => this.json<SourcesReport>("GET", `/v1/analytics/sources${query({ ...r })}`);
  questions = (r: Range) => this.json<{ top: Question[]; unanswered: Question[] }>("GET", `/v1/analytics/top-questions${query({ ...r })}`);
  retention = () => this.json<{ conversationRetentionDays: number }>("GET", "/v1/analytics/settings").then((x) => x.conversationRetentionDays);
  setRetention = (days: number) => this.json<{ conversationRetentionDays: number }>("PUT", "/v1/analytics/settings", { conversationRetentionDays: days });
  conversations = (f: ConvFilters, offset = 0, limit = 25) => this.json<{ conversations: ConvRow[]; total: number }>("GET", `/v1/conversations${query({ ...f, limit, offset })}`);
  conversation = (id: string) => this.json<ConvDetail>("GET", `/v1/conversations/${encodeURIComponent(id)}`);
  remove = (id: string) => this.json<{ ok: true }>("DELETE", `/v1/conversations/${encodeURIComponent(id)}`);

  /** The file for statistics, a filtered list or one conversation. */
  async file(what: { kind: "stats"; range: Range } | { kind: "list"; filters: ConvFilters } | { kind: "one"; id: string }, format: Format): Promise<{ blob: Blob; name: string }> {
    const path =
      what.kind === "stats" ? `/v1/analytics/export/${format}${query({ ...what.range })}` : what.kind === "list" ? `/v1/conversations/export/${format}${query({ ...what.filters })}` : `/v1/conversations/${encodeURIComponent(what.id)}/export/${format}`;
    const res = await this.send("GET", path);
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `nemo-${what.kind}.${format}`;
    return { blob: await res.blob(), name };
  }
}

/** Hands a file to the browser's download. */
export function saveFile(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
